import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, TrainFront, Bus, CarTaxiFront, Plane, Navigation } from 'lucide-react';

const MODE_CONFIG: Record<
  string,
  { icon: React.ElementType; label: string; chipClass: string; iconClass: string }
> = {
  TRAIN: {
    icon: TrainFront,
    label: 'Train',
    chipClass: 'border-blue-200/80 bg-blue-50/90 text-blue-900',
    iconClass: 'text-blue-600',
  },
  BUS: {
    icon: Bus,
    label: 'Bus',
    chipClass: 'border-emerald-200/80 bg-emerald-50/90 text-emerald-900',
    iconClass: 'text-emerald-600',
  },
  TAXI: {
    icon: CarTaxiFront,
    label: 'Taxi',
    chipClass: 'border-amber-200/80 bg-amber-50/90 text-amber-900',
    iconClass: 'text-amber-700',
  },
  AIR: {
    icon: Plane,
    label: 'Air',
    chipClass: 'border-sky-200/80 bg-sky-50/90 text-sky-900',
    iconClass: 'text-sky-600',
  },
};

export interface HowToReachStation {
  name: string;
  options: Array<{ mode: 'TRAIN' | 'BUS' | 'TAXI' | 'AIR'; details?: string }>;
}

interface HowToReachProps {
  stations: HowToReachStation[];
  title?: string;
}

const HowToReach: React.FC<HowToReachProps> = ({ stations, title = 'How to Reach This School' }) => {
  if (!stations || stations.length === 0) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-30px' }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      <div className="w-full py-1">
        <div className="mb-4 flex items-start gap-3 sm:mb-4 sm:items-center">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange/10 text-orange"
            aria-hidden
          >
            <Navigation className="h-5 w-5" strokeWidth={2} />
          </span>
          <div>
            <h2 className="heading-section font-serif font-semibold text-midnight">{title}</h2>
            <p className="mt-1 text-2 text-midnight/60">
              Follow the steps from your arrival point to the school.
            </p>
          </div>
        </div>

        {/* Vertical timeline — reads top to bottom on all screen sizes */}
        <div className="relative pl-1 sm:pl-2">
          <div
            className="absolute left-[1.125rem] top-3 bottom-3 w-px bg-gradient-to-b from-orange/50 via-orange/25 to-orange/10 sm:left-[1.25rem]"
            aria-hidden
          />

          <ol className="relative space-y-0">
            {stations.map((station, idx) => {
              const detail = station.options.find((o) => o.details?.trim())?.details;
              return (
                <motion.li
                  key={`${station.name}-${idx}`}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: idx * 0.06 }}
                  className="relative flex gap-3 pb-8 last:pb-0 sm:gap-4"
                >
                  <div className="relative z-[1] flex w-9 shrink-0 flex-col items-center sm:w-10">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-orange to-orange-dark text-1 font-bold text-white shadow-md ring-4 ring-white sm:h-10 sm:w-10">
                      {idx + 1}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <h3 className="text-2 font-semibold leading-snug text-midnight">{station.name}</h3>
                    {station.options.length > 0 && (
                      <ul className="mt-2 flex flex-wrap gap-2" aria-label="Transport options">
                        {station.options.map((opt, optIdx) => {
                          const config = MODE_CONFIG[opt.mode] || MODE_CONFIG.TRAIN;
                          const Icon = config.icon;
                          return (
                            <li key={optIdx}>
                              <span
                                className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-1 font-semibold shadow-sm ${config.chipClass}`}
                              >
                                <Icon className={`h-3.5 w-3.5 shrink-0 ${config.iconClass}`} strokeWidth={2} />
                                {config.label}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {detail ? (
                      <p className="mt-2 text-2 leading-relaxed text-midnight/60">{detail}</p>
                    ) : null}
                  </div>
                </motion.li>
              );
            })}

            <motion.li
              initial={{ opacity: 0, x: -8 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35, delay: stations.length * 0.06 }}
              className="relative flex gap-3 sm:gap-4"
            >
              <div className="relative z-[1] flex w-9 shrink-0 justify-center sm:w-10">
                <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-orange/45 bg-orange/5 text-orange ring-4 ring-white sm:h-10 sm:w-10">
                  <MapPin className="h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" strokeWidth={2} />
                </span>
              </div>
              <div className="min-w-0 flex-1 pb-1 pt-0.5">
                <h3 className="text-2 font-semibold text-midnight">School</h3>
                <p className="mt-1 text-2 leading-relaxed text-midnight/65">You&apos;ve reached the yoga school.</p>
              </div>
            </motion.li>
          </ol>
        </div>
      </div>
    </motion.section>
  );
};

export default HowToReach;
